/* src/services/AuthService.js
   Authentification via httpOnly cookie – alignée avec server.js
*/

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authorizedFiles = [];
    this.organizedData = {};
    this.isLoaded = false;

    // Restore non-sensitive user info for immediate UI render.
    // Cookie validity is confirmed asynchronously via restoreSession().
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('reputai_user');
      if (storedUser) {
        try {
          this.currentUser = JSON.parse(storedUser);
        } catch {
          localStorage.removeItem('reputai_user');
        }
      }
    }
  }

  /* =========================
     FETCH UTILITAIRE (cookie)
  ========================= */

  async fetchWithAuth(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
    });

    if (response.status === 401) {
      console.warn('Session expirée ou invalide → logout');
      this.clearSession();
      throw new Error('Session expirée');
    }

    return response;
  }

  /* =========================
      FETCH FICHIER D'ANALYSE
    ========================= */

  async fetchAnalysisFile(filename) {
    const res = await this.fetchWithAuth(
      `/api/analysis/file/${encodeURIComponent(filename)}`
    )

    if (res.status === 404) return null

    if (!res.ok) {
      throw new Error(`Impossible de charger ${filename}`)
    }

    return res.json()
  }

  /* =========================
     AUTH
  ========================= */

  async login(username, password) {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return null;
      }

      // Cookie is set by the server. Store only non-sensitive user info locally.
      this.currentUser = result.user;
      localStorage.setItem('reputai_user', JSON.stringify(this.currentUser));

      this.authorizedFiles = [];
      this.organizedData = {};
      this.isLoaded = false;

      await this.loadAuthorizedData();
      return this.currentUser;
    } catch (err) {
      console.error('Erreur login:', err);
      return null;
    }
  }

  async publicLogin(username) {
    try {
      const response = await fetch('/api/auth/public-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) return null;

      this.currentUser = result.user;
      localStorage.setItem('reputai_user', JSON.stringify(this.currentUser));
      this.authorizedFiles = [];
      this.organizedData = {};
      this.isLoaded = false;
      await this.loadAuthorizedData();
      return this.currentUser;
    } catch (err) {
      console.error('Erreur public login:', err);
      return null;
    }
  }

  async restoreSession() {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include' });
      if (!response.ok) {
        this.clearSession();
        return null;
      }
      const result = await response.json();
      this.currentUser = result.user;
      localStorage.setItem('reputai_user', JSON.stringify(this.currentUser));
      return this.currentUser;
    } catch {
      this.clearSession();
      return null;
    }
  }

  isAuthenticated() {
    return !!this.currentUser;
  }

  async logout() {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — clear local state regardless
    }
    this.clearSession();
  }

  clearSession() {
    this.currentUser = null;
    this.authorizedFiles = [];
    this.organizedData = {};
    this.isLoaded = false;

    if (typeof window !== 'undefined') {
      localStorage.removeItem('reputai_user');
    }
  }

  /* =========================
     DATA AUTORISÉES
  ========================= */

  async loadAuthorizedData() {
    try {
      const filesRes = await this.fetchWithAuth('/api/user/authorized-files');
      const filesJson = await filesRes.json();

      if (filesJson.success) {
        this.authorizedFiles = filesJson.files;
      }

      const dataRes = await this.fetchWithAuth('/api/user/organized-data');
      const dataJson = await dataRes.json();

      if (dataJson.success) {
        this.organizedData = dataJson.organizedData;
      }

      this.isLoaded = true;
      return true;
    } catch (err) {
      console.error('Erreur chargement données autorisées:', err);
      return false;
    }
  }

  async refreshData() {
    if (!this.currentUser) return false;
    return this.loadAuthorizedData();
  }

  /* =========================
     GETTERS
  ========================= */

  getCurrentUser() {
    return this.currentUser;
  }

  getAuthorizedFiles() {
    return this.authorizedFiles;
  }

  getOrganizedData() {
    return this.organizedData;
  }

  hasAccessToFile(filename) {
    return this.authorizedFiles.includes(filename);
  }

  /* =========================
     UTILITAIRES DATES
  ========================= */

  formatDateForDisplay(date) {
    if (date === 'default') return 'Général';

    const [month, year] = date.split('/');
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
    ];

    return `${months[parseInt(month, 10) - 1]} ${year}`;
  }

  /* =========================
     DEBUG
  ========================= */

  getDebugInfo() {
    return {
      authenticated: !!this.currentUser,
      user: this.currentUser,
      authorizedFiles: this.authorizedFiles.length,
      companies: Object.keys(this.organizedData),
      loaded: this.isLoaded,
    };
  }
}

const authService = new AuthService();
export default authService;
